import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StorefrontService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getStoreBySubdomain(store: string): Observable<Storefront> {
    return this.http.get<Storefront>(`${this.apiUrl}/companies/by-suburl/${store}`);
  }

  /** Tells the backend which page a visitor came from: a link on the company's own
   * website marks the shop as integrated (checklist task, team notification). */
  reportVisit(store: string, referrer: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/companies/by-suburl/${store}/visit`, {
      referrer,
    });
  }

  // ===== Subdomain Detection Methods =====

  /**
   * Checks if the current hostname matches a specific subdomain
   * @param subdomain The subdomain to check (e.g., "gutscheine")
   * @returns true if the current URL matches the subdomain
   *
   * Examples:
   * - isSubdomain("gutscheine") → true on gutscheine.schenkwerk.com
   * - isSubdomain("gutscheine") → true on gutscheine.localhost:4200
   * - isSubdomain("gutscheine") → false on dashboard.schenkwerk.com
   */
  isSubdomain(subdomain: string): boolean {
    const hostname = window.location.hostname;
    const port = window.location.port;

    // Build expected subdomain host
    const baseDomain = environment.storefrontBaseDomain.split(':')[0];
    const expectedHost = `${subdomain}.${baseDomain}`;

    // Check exact match (with or without port)
    if (hostname === expectedHost) {
      return true;
    }

    // Also check with port if present
    const currentHostWithPort = port ? `${hostname}:${port}` : hostname;
    const expectedHostWithPort = port ? `${expectedHost}:${port}` : expectedHost;

    if (currentHostWithPort === expectedHostWithPort) {
      return true;
    }

    return false;
  }

  // ===== Storefront Detection Methods =====

  /**
   * Determines if the current URL is a storefront (not the dashboard or other special subdomains)
   */
  isStorefront(): boolean {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const fullUrl = window.location.href;

    // Helper function to normalize hostname (add port if needed for comparison)
    const getCurrentHostWithPort = () => {
      return port ? `${hostname}:${port}` : hostname;
    };

    // Helper function to extract hostname from a URL that might include port
    const extractHostname = (url: string) => {
      return url.split(':')[0];
    };

    const currentHostWithPort = getCurrentHostWithPort();
    const dashboardHostname = extractHostname(environment.dashboardUrl);

    // FIRST: Check if current host is the dashboard URL (exact match with highest priority)
    if (currentHostWithPort === environment.dashboardUrl || hostname === dashboardHostname) {
      return false;
    }

    // SECOND: Check if we're on special subdomains (gutscheine, etc.) - these are NOT storefronts
    if (this.isSubdomain('gutscheine')) {
      return false;
    }

    // THIRD: For development mode, check localhost patterns
    if (hostname.includes('localhost') || environment.storefrontBaseDomain.includes('localhost')) {
      // For localhost, if it's a subdomain of localhost and not the dashboard, it's a storefront
      if (hostname.endsWith('.localhost')) {
        return true;
      }

      // Check if current host ends with the storefront base domain pattern
      const basePattern = environment.storefrontBaseDomain.split(':')[0]; // Remove port for pattern matching
      if (hostname.endsWith(basePattern) && currentHostWithPort !== environment.dashboardUrl) {
        return true;
      }
    }

    // FOURTH: For staging/production - CRITICAL FIX: Check exact dashboard match again
    // before checking storefront pattern to prevent false positives
    if (hostname === environment.dashboardUrl.split(':')[0]) {
      return false;
    }

    // FIFTH: Check if hostname ends with the storefront base domain (but is NOT the dashboard)
    if (hostname.endsWith(environment.storefrontBaseDomain.split(':')[0])) {
      return true;
    }

    return false;
  }

  /**
   * Extracts the company name from the storefront URL
   * Examples:
   * - indiejobs.schenkwerk.com → "indiejobs"
   * - company.staging.schenkwerk.com → "company"
   */
  getStorefrontCompany(): string | null {
    if (!this.isStorefront()) {
      return null;
    }

    const hostname = window.location.hostname;

    // Extract company name from subdomain
    const parts = hostname.split('.');

    if (parts.length > 0) {
      const companyName = parts[0];
      return companyName;
    }

    return null;
  }

  /**
   * Gets the current hostname
   */
  getCurrentHostname(): string {
    return window.location.hostname;
  }

  /**
   * Gets the dashboard URL for the current environment
   */
  getDashboardUrl(): string {
    return environment.dashboardUrl;
  }

  /**
   * Gets the storefront base domain for the current environment
   */
  getStorefrontBaseDomain(): string {
    return environment.storefrontBaseDomain;
  }

  /**
   * Gets the protocol for the current environment
   */
  getProtocol(): string {
    return environment.protocol;
  }

  /**
   * Generates the full storefront URL for a given company
   * @param companyName The company subdomain name
   * @returns The complete storefront URL
   */
  generateStorefrontUrl(companyName: string): string {
    const protocol = this.getProtocol();
    const baseDomain = this.getStorefrontBaseDomain();
    return `${protocol}${companyName}.${baseDomain}`;
  }

  /**
   * Gets the current storefront URL if we're on a storefront
   * @returns The current storefront URL or null if not on a storefront
   */
  getCurrentStorefrontUrl(): string | null {
    if (!this.isStorefront()) {
      return null;
    }

    const companyName = this.getStorefrontCompany();
    if (!companyName) {
      return null;
    }

    return this.generateStorefrontUrl(companyName);
  }

  /**
   * Gets the full URL for a company image
   * @param imagePath The image path from the company data
   * @returns The full URL to access the image
   */
  getImageUrl(imagePath: string): string {
    if (!imagePath) return '';
    // Remove 'uploads/' prefix if present since the API endpoint already includes it
    const cleanPath = imagePath.startsWith('uploads/') ? imagePath.substring(8) : imagePath;
    return `${this.apiUrl}/images/${cleanPath}`;
  }
}

export interface Storefront {
  name: string;
  email: string;
  description: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  website: string;
  suburl: string;
  public_id: string;
  created_at: string;
  is_storefront_enabled: boolean;
  logo_image_path?: string;
  banner_image_path?: string;
  latitude?: number;
  longitude?: number;
  // Voucher app configuration
  voucher_app_title?: string;
  voucher_app_description?: string;
  minimum_voucher_amount?: number; // Amount in cents
  voucher_app_primary_color?: string; // Hex color e.g. #FF0000 (widget button color)
  voucher_app_text_color?: string; // Hex color e.g. #FFFFFF (widget text color)
  company_covers_customer_fee?: boolean;
  voucher_products_enabled?: boolean;
  money_voucher_enabled?: boolean;
  offered_fixed_voucher_amounts?: number[]; // cents; empty while the fixed-amount card is off
}
